import React, { ComponentProps } from 'react';
import { RUN_CODE_LENGTH_LIMIT } from '@yandex-int/k-common';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';
import { indentUnit } from '@codemirror/language';
import { Code, errorLineHighlightExtension } from '@edu-frontend/modules/src/shared/ui/Code';
import { Extension } from '@codemirror/state';
import { CodingVerdict } from '../../../__verdict/coding-problem__verdict';
import { pasteTabsAsSpaces } from '../../_lib/paste-tabs-as-spaces-extension';
import { useCodingProblemProps } from '../../../context';
import { useCodingStore } from '../../../state';

// 4 пробела - отступ в редакторе
const INDENT_UNIT = '    ';

interface CodingCodeEditorDesktopProps {
    initialValue: string;
    onChange?: ComponentProps<typeof Code>['onChange'];
    readOnly?: boolean;
    extensions: Extension[];
    initialState: ComponentProps<typeof Code>['initialState'];
}

export function CodeEditorDesktop({
    initialValue,
    readOnly,
    onChange,
    extensions: extensionsFromProps,
    initialState,
}: CodingCodeEditorDesktopProps) {
    const props = useCodingProblemProps();
    const { isSelectBlocked, teacherMode, attempt } = props;

    const selectedTest = useCodingStore((store) => store.selectedTest);
    const checkDetails = attempt?.markers?.result?.[selectedTest]?.check_details;

    // Номер строки с ошибкой. errorLineNumber >= 1
    const errorLineNumber = checkDetails?.exception_line;

    const extensions = React.useMemo(
        () => [
            ...extensionsFromProps,
            // Задаем отступы в 4 пробела
            indentUnit.of(INDENT_UNIT),
            // Обрабатываем вставку текста из буфера обмена (заменяем табы на пробелы - см. https://st.yandex-team.ru/EDUCATION-24690)
            pasteTabsAsSpaces(INDENT_UNIT),
            // Подсветка линии с ошибкой
            errorLineHighlightExtension(errorLineNumber),
        ],
        [errorLineNumber, extensionsFromProps]
    );

    return (
        <>
            <ThemeProvider isRoot={false} theme={hrLight}>
                <Code
                    disableSelection={isSelectBlocked}
                    editable={!readOnly}
                    extensions={extensions}
                    initialState={initialState}
                    key={props.showCorrectAnswers ? 'correctAnswer' : 'answer'}
                    maxLength={RUN_CODE_LENGTH_LIMIT}
                    minHeight="200px"
                    onChange={onChange}
                    options={{
                        foldGutter: false,
                        allowMultipleSelections: true,
                        lineNumbers: true,
                        autocompletion: false,
                        tabSize: 4,
                        bracketMatching: true,
                        highlightActiveLineGutter: !readOnly && !isSelectBlocked,
                        highlightActiveLine: !readOnly && !isSelectBlocked,
                        // History extension is provided separately to accept custom params
                        history: false,
                    }}
                    styleSettings={{
                        fontSize: '16px',
                        lineHeight: '1.4',
                        padding: `${teacherMode ? 20 : 100}px 0 0`,
                    }}
                    value={initialValue}
                    withSyntaxHighlight
                />
            </ThemeProvider>
            <CodingVerdict />
        </>
    );
}
